import { DatePipe } from '@angular/common';
import { Component, ComponentFactoryResolver, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

import { take } from 'rxjs/operators';
import { ApiError, AuthService, ConfigService, DataTablesService, MessageService, Task, TaskService, VisualizacaoStorageService } from '../../../../core';
import { EscalaRentabilidadeComponent } from '../../../../domains/core/settings/grupos-rentabilidade/components/escala-rentabilidade/escala-rentabilidade.component';
import { CurrencyFormatPipe, DecimalFormatPipe } from '../../../../shared';
import { ConfigEmpresa, isConfigEmpresaVersaoIntegracaoErp1 } from '../../../configuracoes/app/empresa';
import { ConfigPedido, ConfigPedidoTipoVendaBadgeComponent, isModoTabelaPreco, isVenda } from '../../../configuracoes/app/pedido';
import { IntegrationPedidoHttpService } from '../../../configuracoes/integration/pedido';
import { ExpedicaoHttpService } from '../../../expedicao/services';
import { KPI_CUSTOMER_SHARE, KPI_RAP } from '../../../kpis';
import { ClienteNomePipe, ClienteNomeSecundarioPipe } from '../../../pessoa/cliente/pipes';
import { RepresentanteNomePipe, RepresentanteNomeSecundarioPipe } from '../../../pessoa/representante/pipes';
import { CustomerShareIndicadorPipe } from '../../../pic/customer-share/indicador/pipes';
import { UsuarioPerfil } from '../../../usuario/usuario-perfil';
import { PedidoCancelamentoModalComponent } from '../../cancelamento';
import { PedidoTableBadgesContainerComponent } from '../../components/table-badges-container';
import { PedidoConfirmarHttpService } from '../../confirmar';
import { PedidoDRPLucroLiquidoBadgeComponent } from '../../drp/components/lucro-liquido-badge';
import { PedidoEmailModalComponent } from '../../email';
import { PedidoFilter } from '../../filter';
import { PedidoSetorGepBadgeComponent } from '../../gep/setor-gep';
import { PedidoSetorGepAguardandoProducaoHttpService } from '../../gep/setor-gep/aguardando-producao';
import { PedidoIntegracaoEcommerceImportHttpService, PedidoIntegracaoEcommerceImportService } from '../../integracoes/ecommerce/import';
import { PedidoIntegracaoErpImportHttpService, PedidoIntegracaoErpImportService } from '../../integracoes/erp/import';
import { PedidoIntegrationErpService } from '../../integration-erp';
import { PedidoLiberarModalComponent } from '../../nao-liberado/components/liberar-modal';
import { PedidoMotivoNaoLiberadoAbreviacaoPipe } from '../../nao-liberado/pipes';
import { PedidoIsLiberadoService, PedidoNaoLiberadoHttpService } from '../../nao-liberado/services';
import { Pedido } from '../../pedido';
import { PedidoSearch } from '../../pedido-search';
import { PedidoSearchOrderBy } from '../../pedido-search-orderby';
import { PedidoSearchTotal } from '../../pedido-search-total';
import { PedidoReportService } from '../../report';
import { PedidoAuthGuardService, PedidoRouterService, PedidoSearchHttpService } from '../../services';
import { PedidoStatusBadgeComponent, pedidoConfirmado } from '../../status';
import { PedidoPedidoOptions, PedidoPedidoOptionsActions } from '../pedido-pedido-options';
import { PedidoPedidoListColumns } from './pedido-pedido-list-columns';

@Component({
    selector: 'sgt-pedido-pedido-list',
    templateUrl : './pedido-pedido-list.component.html',
    styleUrls: ['./pedido-pedido-list.component.less'],
    providers: [
        ClienteNomePipe,
        ClienteNomeSecundarioPipe,
        RepresentanteNomePipe,
        RepresentanteNomeSecundarioPipe,
        PedidoMotivoNaoLiberadoAbreviacaoPipe,
        CustomerShareIndicadorPipe
    ]
})
export class PedidoPedidoListComponent implements OnInit, OnDestroy {
    configEmpresa: ConfigEmpresa;
    isIntegracaoErpV1: boolean;
    configPedido: ConfigPedido;
    isAdministrador: boolean;
    isRepresentante: boolean;
    isAtendente: boolean;
    isGerente: boolean;
    isPCP: boolean;
    usuarioPodeLiberarPedido: boolean;
    perfilUsuario: UsuarioPerfil;

    /** Subscriptions das pesquisas. Utilizado para realizar o unsubscribe e cancelar o retorno de requests de pesquissa pendentes antes de iniciar novos requests */
    private searchSubs$: Subscription;


    // Entities
    isLoading: boolean;
    // Total
    isLoadingTotal: boolean;
    total: PedidoSearchTotal;

    private dataTable: any;
    orderBy: number;
    orderByDir: string;

    @Input() tableId = 'pedidoPedidoListTable';
    @Input() visualizacaoStorageKey = 'pedidoPedidoListVisualizacao';
    @Input() filter: PedidoFilter;
    /** Ações disponíveis */
    @Input() optionsVisibility: PedidoPedidoOptionsActions = {
        canEdit: true,
        canConsultar: true,
        canConfirmar: true,
        canExportOneErp: true,
        canEnviarEmail: true,
        canPrint: true,
        canDuplicate: true,
        canLiberar: true,
        canIniciarAguardandoProducao: true,
        canTerminarAguardandoProducao: true,
        canLiberarExpedicao: true,
        canCancelar: true,
        canImportOneEcommerce: true,
        canImportOneErp_v1: true,
        canImportOneErp: true,
        canOpenRetornoAmostras: true,
        // Orçamento
        canSolicitarRevisao: true,
        canRevisar: true,
        canConfirmarPCP: true,
        // Proposta Comercial
        canPrintPropostaComercial: true,
        canSendEmailPropostaComercial: true,
        canRetornarAnaliseCliente: true
    }
    /** Colunas visíveis na table */
    @Input() columnsVisibility: PedidoPedidoListColumns = {
        options: true,
        badges: true,
        lucroLiquido: true,
        numero: true,
        numeroEcommerce: true,
        numeroERP: true,
        dataEmissao: true,
        dataConfirmacao: true,
        quantidadeTotal: true,
        quantidadeVolumesPrevistos: true,
        valorTotal: true,
        status: true,
        setorGep: true,
        cliente: true,
        classificacaoFaturamentoPretendida: true,
        representante: true,
        motivoReprovado: true,
        tabelaPreco: true,
        campanha: true,
        condicaoPagamento: true,
        motivoNaoLiberado: false,
        dataNaoLiberado: false,
        vendaReferenciada: false,
        lucroLiquidoNaoLiberado: false,
        dataLiberado: true,
        usuarioLiberado: true,
        justificativaLiberado: true
    };

    /** Emite evento após liberar um pedido */
    @Output() afterLiberarPedido = new EventEmitter<Pedido>();
    /** Emite evento após cancelar um pedido */
    @Output() afterCancelar = new EventEmitter<void>();

    /** Container para criar dinamicamente os badges na table */
    @ViewChild('colBadgesContainer', {read: ViewContainerRef}) colBadgesContainer: ViewContainerRef;
    /** Container para criar dinamicamente o badge do pedido fracionado */
    @ViewChild('colBadgePedidoFracionadoContainer', {read: ViewContainerRef}) colBadgePedidoFracionadoContainer: ViewContainerRef;
    /** Container para criar dinamicamente os badges dos tipos de vendas dos pedidos */
    @ViewChild('tipoVendaBadgesContainer', {read: ViewContainerRef}) tipoVendaBadgesContainer: ViewContainerRef;
    /** Container para criar dinamicamente os badges dos status dos pedidos */
    @ViewChild('statusBadgesContainer', {read: ViewContainerRef}) statusBadgesContainer: ViewContainerRef;
    /** Container para criar dinamicamente os badges dos setores GEPs dos pedidos */
    @ViewChild('setorGepBadgesContainer', {read: ViewContainerRef}) setorGepBadgesContainer: ViewContainerRef;
    /** Modal para envio de e-mails */
    @ViewChild('pedidoEmailModal') pedidoEmailModal: PedidoEmailModalComponent;
    /** Modal para liberar pedidos */
    @ViewChild('pedidoLiberarModal') pedidoLiberarModal: PedidoLiberarModalComponent;
    /** Modal para cancelar os pedidos */
    @ViewChild('pedidoCancelamentoModal') pedidoCancelamentoModal: PedidoCancelamentoModalComponent;

    /** Subscription da execução da importação dos pedidos do e-commerce */
    afterImportEcommerceSub$: Subscription;
    /** Subscription da execução da importação dos pedidos do ERP */
    afterImportErpSub$: Subscription;
    /** Subscription da execução da exportação do pedido para o ERP */
    afterExportOneErp$: Subscription;

    // KPIs
    KPI_RAP = KPI_RAP;

    constructor(private activatedRoute: ActivatedRoute,
                private el: ElementRef,
                private componentFactoryResolver: ComponentFactoryResolver,
                private renderer: Renderer2,
                private authService: AuthService,
                private configService: ConfigService,
                private dataTablesService: DataTablesService,
                private messageService: MessageService,
                private visualizacaoStorageService: VisualizacaoStorageService,
                private taskService: TaskService,
                private datePipe: DatePipe,
                private decimalFormatPipe: DecimalFormatPipe,
                private currencyFormatPipe: CurrencyFormatPipe,
                private clienteNomePipe: ClienteNomePipe,
                private clienteNomeSecundarioPipe: ClienteNomeSecundarioPipe,
                private customerShareIndicadorPipe: CustomerShareIndicadorPipe,
                private representanteNomePipe: RepresentanteNomePipe,
                private representanteNomeSecundarioPipe: RepresentanteNomeSecundarioPipe,
                private pedidoAuthGuardService: PedidoAuthGuardService,
                private pedidoRouterService: PedidoRouterService,
                private pedidoSearchHttpService: PedidoSearchHttpService,
                private pedidoConfirmarHttpService: PedidoConfirmarHttpService,
                private pedidoReportService: PedidoReportService,
                private expedicaoHttpService: ExpedicaoHttpService,
                private pedidoIntegrationErpService: PedidoIntegrationErpService,
                private integrationPedidoHttpService: IntegrationPedidoHttpService,
                private pedidoIntegracaoEcommerceImportService: PedidoIntegracaoEcommerceImportService,
                private pedidoIntegracaoEcommerceImportHttpService: PedidoIntegracaoEcommerceImportHttpService,
                private pedidoIntegracaoErpImportService: PedidoIntegracaoErpImportService,
                private pedidoIntegracaoErpImportHttpService: PedidoIntegracaoErpImportHttpService,
                private pedidoIsLiberadoService: PedidoIsLiberadoService,
                private pedidoNaoLiberadoHttpService: PedidoNaoLiberadoHttpService,
                private pedidoSetorGepAguardandoProducaoHttpService: PedidoSetorGepAguardandoProducaoHttpService,
                private pedidoMotivoNaoLiberadoAbreviacaoPipe: PedidoMotivoNaoLiberadoAbreviacaoPipe) { }

    ngOnInit() {
        this.configEmpresa = this.configService.getEmpresa();
        this.isIntegracaoErpV1 = isConfigEmpresaVersaoIntegracaoErp1(this.configEmpresa);
        this.configPedido = this.configService.getPedido();
        this.isAdministrador = this.authService.isAdministrador();
        this.isRepresentante = this.authService.isRepresentante();
        this.isAtendente = this.authService.isAtendente();
        this.isGerente = this.authService.isGerente();
        this.isPCP = this.authService.isPCP();
        this.usuarioPodeLiberarPedido = this.authService.podeLiberarPedido();
        this.perfilUsuario = this.authService.getPerfilUsuario();

        // Subscrevendo ao evento de finalização da importação dos pedidos do e-commerce
        this.afterImportEcommerce();
        // Subscrevendo ao evento de finalização da importação dos pedidos do ERP
        this.afterImportErp();
        // Subscrevendo ao evento de exportação do pedido para o ERP
        this.afterExportOneErp();
    }

    search() {
        // Realizando unsubscribe dos subscriptions atuais e criando um novo para as próximas pesquisas
        this.searchSubs$?.unsubscribe();
        this.searchSubs$ = new Subscription();

        // Obtendo os totais de pedidos
        this.getTotais()
            .then(() => {
                if (!this.dataTable) { // Table ainda não criada
                    // Obtendo os dados da datatable armazenados no storage para sabermos o order e posição do scroll
                    const vs = this.visualizacaoStorageService.get(this.visualizacaoStorageKey);

                    this.createTable(vs?.order)
                        .then(() => {
                            // Realizando scroll para a posição inicial salva
                            if (vs?.scrollerStartPosition > this.dataTable?.scroller?.page()?.end) {
                                this.dataTable?.scroller?.toPosition(vs?.scrollerStartPosition);
                            }
                        });
                } else { // Table já criada, somente damos um reload
                    this.dataTable.ajax.reload();
                }
            })
            .catch(() => {});
    }

    /** Pesquisa todos os pedidos sem realizar a paginação */
    searchAll(): Promise<PedidoSearch[]> {
        return new Promise<PedidoSearch[]>((resolve, reject) => {
            this.pedidoSearchHttpService.search(null, this.filter, this.orderBy, this.orderByDir, -1, -1)
                .pipe(
                    take(1)
                )
                .subscribe(
                    pedidos => resolve(pedidos),
                    (error: ApiError) => reject(error)
                )
        });
    }

    /** Obtem os totais dos pedidos */
    private getTotais(): Promise<PedidoSearchTotal> {
        return new Promise<PedidoSearchTotal>((resolve, reject) => {
            this.searchSubs$.add(this.pedidoSearchHttpService.getTotais(null, this.filter)
                .subscribe(
                    total => {
                        this.total = total;
                        this.isLoadingTotal = false;
                        resolve(total);
                    },
                    (error: ApiError) => {
                        this.isLoadingTotal = false;
                        this.messageService.error(error.message, 'Erro ao obter os totais dos pedidos', error);
                        reject();
                    }
                )
            );

        })
    }

    /** Cria os badges e insere na table */
    private createBadges(entity: PedidoSearch, td: any) {
        const badgeFactory = this.componentFactoryResolver.resolveComponentFactory(PedidoTableBadgesContainerComponent);
        const badgeRef = this.colBadgesContainer.createComponent(badgeFactory);
        badgeRef.instance.pedido = entity;
        badgeRef.instance.isIntegracaoErpV1 = this.isIntegracaoErpV1;

        this.renderer.appendChild(td, badgeRef.location.nativeElement);
    }

    /** Limpa o container dos badges criados e inseridos dinamicamente na table */
    private clearBadges() {
        this.colBadgesContainer?.clear();
    }


    /** Cria o badge do lucro líquido do pedido na table */
    private createBadgeLucroLiquido(entity: PedidoSearch, td: any) {
        if (!entity) {
            return;
        }
        // Se não puder visualizar o D.R.P, não exibe o badge do lucro líquido
        if (!this.pedidoAuthGuardService.canShowDRP(entity.tipoVenda)) {
            return;
        }

        const badgeFactory = this.componentFactoryResolver.resolveComponentFactory(PedidoDRPLucroLiquidoBadgeComponent);
        const badgeRef = this.colBadgesContainer.createComponent(badgeFactory);
        badgeRef.instance.valorLucroLiquido = entity.valorLucroLiquido;
        badgeRef.instance.percLucroLiquido = entity.percLucroLiquido;
        badgeRef.instance.percLucroLiquidoAbaixo = entity.percLucroLiquidoAbaixo;

        // Inserindo o badge na table cell
        this.renderer.appendChild(td, badgeRef.location.nativeElement);
    }

    /** Cria o badge do lucro líquido do pedido na table */
    private createBadgeEscalaGrupoRentabilidade(entity: PedidoSearch, td: any) {
        if (!entity || !entity.idGrupoRentabilidade || !entity.escalaGrupoRentabilidade) {
            return;
        }
        // Se não puder visualizar o QV, não exibe o badge da escala do grupo de rentabilidade
        if (!this.pedidoAuthGuardService.canShowQV(entity.tipoVenda)) {
             return;
        }

        const badgeFactory = this.componentFactoryResolver.resolveComponentFactory(EscalaRentabilidadeComponent);
        const badgeRef = this.colBadgesContainer.createComponent(badgeFactory);
        badgeRef.instance.id = entity.escalaGrupoRentabilidade;

        // Inserindo o badge na table cell
        this.renderer.appendChild(td, badgeRef.location.nativeElement);
    }


    /** Limpa o container dos badges dos tipos de vendas dos pedidos criados dinamicamente e inseridos na table **/
    private clearTipoVendaBadges() {
        this.tipoVendaBadgesContainer?.clear();
    }

    /** Limpa o container dos badges dos status dos pedidos criados dinamicamente e inseridos na table **/
    private clearStatusBadges() {
        this.statusBadgesContainer?.clear();
    }

    /** Limpa o container dos badges dos setores GEPs dos pedidos criados dinamicamente e inseridos na table **/
    private clearSetorGepBadges() {
        this.setorGepBadgesContainer?.clear();
    }

    /** Cria a dataTable dos pedidos */
    private createTable(initOrder: any[] = null): Promise<void> {
        return new Promise<void>((resolve) => {
            const columns: any[] = [
                {
                    data: null,
                    className: 'col-options',
                    orderable: false,
                    render: (data, type, row, meta) => {
                        const entity = data as PedidoSearch;

                        // Opções
                        const options = new PedidoPedidoOptions();
                        // Editar
                        options.canEdit = this.optionsVisibility.canEdit && (this.canEdit(entity) || this.canEditEntregas(entity));
                        // Consultar
                        options.canConsultar = this.optionsVisibility.canConsultar && !options.canEdit;
                        // Confirmar
                        options.canConfirmar = this.optionsVisibility.canConfirmar && this.pedidoAuthGuardService.canConfirmar(entity);
                        // Export One ERP
                        options.canExportOneErp = this.optionsVisibility.canExportOneErp && this.pedidoAuthGuardService.canExportOneErp(entity, true, this.configPedido);
                        // E-mail
                        options.canEnviarEmail = this.optionsVisibility.canEnviarEmail && this.pedidoAuthGuardService.canSendEmail(entity, false);
                        // Imprimir
                        options.canPrint = this.optionsVisibility.canPrint && this.pedidoAuthGuardService.canPrint(entity, false);
                        // Duplicar
                        options.canDuplicate = this.optionsVisibility.canDuplicate && this.pedidoAuthGuardService.canDuplicate(entity);
                        // Liberar Pedido
                        options.canLiberar = this.optionsVisibility.canLiberar && this.pedidoAuthGuardService.canLiberar(entity)
                        // Iniciar Aguardando Produção
                        options.canIniciarAguardandoProducao = this.optionsVisibility.canIniciarAguardandoProducao && this.pedidoAuthGuardService.canIniciarAguardandoProducao(entity, this.perfilUsuario);
                        // Terminar Aguardando Produção
                        options.canTerminarAguardandoProducao = this.optionsVisibility.canTerminarAguardandoProducao && this.pedidoAuthGuardService.canTerminarAguardandoProducao(entity, this.perfilUsuario);
                        // Liberar Expedição
                        options.canLiberarExpedicao = this.optionsVisibility.canLiberarExpedicao && this.pedidoAuthGuardService.canLiberarExpedicao(entity, this.isAdministrador, this.isAtendente);
                        // Cancelar
                        options.canCancelar = this.optionsVisibility.canCancelar && this.pedidoAuthGuardService.canCancelar(entity, this.isAdministrador, this.isAtendente, this.isRepresentante);
                        // Impportar do E-commerce
                        options.canImportOneEcommerce = this.optionsVisibility.canImportOneEcommerce && this.pedidoAuthGuardService.canImportOneEcommerce(entity);
                        // Impportar do ERP V1
                        options.canImportOneErp_v1 = this.optionsVisibility.canImportOneErp_v1 && this.pedidoAuthGuardService.canImportOneErp_v1(entity, this.isIntegracaoErpV1, this.isAdministrador, this.isAtendente);
                        // Impportar do ERP
                        options.canImportOneErp = this.optionsVisibility.canImportOneErp && this.pedidoAuthGuardService.canImportOneErp(entity, this.isIntegracaoErpV1);
                        // Retorno das amostras
                        options.canOpenRetornoAmostras = this.optionsVisibility.canOpenRetornoAmostras && this.pedidoAuthGuardService.canOpenRetornoAmostras(entity, this.configPedido);

                        let template = '';
                        if (options.hasAnyOption()) {
                            template += `
                                <span class="btn-group dropup table-options">
                                    <button type="button" title="Opções" class="btn btn-link btn-options dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                        <i class="fas fa-ellipsis-h" aria-hidden="true"></i>
                                    </button>
                                    <div class="dropdown-menu">
                            `;
                            // Editar
                            if (options.canEdit) {
                                template +=  `
                                    <a class="dropdown-item" id="edit">
                                        <i class="fas fa-pencil-alt" aria-hidden="true"></i> Editar
                                    </a>
                                `;
                            }
                            // Consultar
                            if (options.canConsultar) {
                                template += `
                                    <a class="dropdown-item" id="consultar">
                                        <i class="fas fa-search" aria-hidden="true"></i> Consultar
                                    </a>
                                `;
                            }
                            // Confirmar
                            if (options.canConfirmar) {
                                template += `
                                    <a class="dropdown-item" id="confirmar">
                                        <i class="far fa-handshake"></i> Confirmar
                                    </a>
                                `;
                            }
                            // Export ERP
                            if (options.canExportOneErp) {
                                template += `
                                    <a class="dropdown-item dropdown-item-success" id="exportOneErp">
                                        <i class="fas fa-upload"></i> Exportar para ERP
                                    </a>
                                `;
                            }

                            // Liberar pedido
                            if (options.canLiberar) {
                                template += `
                                    <a class="dropdown-item dropdown-item-warning" id="liberar">
                                        <i class="far fa-thumbs-up"></i> Abrir Liberação do Pedido
                                    </a>
                                `;
                            }

                            // Iniciar Aguardando Produção
                            if (options.canIniciarAguardandoProducao) {
                                template += `
                                    <a class="dropdown-item" id="iniciarAguardandoProducao">
                                        <i class="fas fa-hourglass-half"></i> Aguardar Produção
                                    </a>
                                `;
                            }
                            // Terminar Aguardando Produção
                            if (options.canTerminarAguardandoProducao) {
                                template += `
                                    <a class="dropdown-item" id="terminarAguardandoProducao">
                                        <i class="far fa-check-circle"></i> Produção Liberada
                                    </a>
                                `;
                            }


                            // Liberar para Expedição
                            if (options.canLiberarExpedicao) {
                                template += `
                                    <a class="dropdown-item" id="liberarExpedicao">
                                        <i class="fas fa-cubes" aria-hidden="true"></i> Liberar Expedição
                                    </a>
                                `;
                            }

                            // Retorno das amostras
                            if (options.canOpenRetornoAmostras) {
                                template += `
                                    <a class="dropdown-item" id="openRetornoAmostras">
                                        <i class="far fa-list-alt"></i> Retorno das Amostras
                                    </a>
                                `;
                            }

                            template += `
                                <div class="dropdown-divider"></div>
                            `;

                            // E-mail
                            if (options.canEnviarEmail) {
                                template += `
                                    <a class="dropdown-item" id="email">
                                        <i class="far fa-envelope" aria-hidden="true"></i> Enviar E-mail
                                    </a>
                                `;
                            }
                            // Imprimir
                            if (options.canPrint) {
                                template += `
                                    <a class="dropdown-item" id="print">
                                        <i class="fas fa-print" aria-hidden="true"></i> Imprimir
                                    </a>
                                `;
                            }
                            // Duplicar pedido
                            if (options.canDuplicate) {
                                template += `
                                    <a class="dropdown-item" id="duplicate">
                                        <i class="far fa-copy" aria-hidden="true"></i> Duplicar Pedido
                                    </a>
                                `;
                            }

                            // Import One E-commerce
                            if (options.canImportOneEcommerce) {
                                template += `
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item" id="importOneEcommerce" title="Importar do E-commerce"><i class="fas fa-download"></i> Importar do E-commerce</a>
                                `;
                            }

                            // Import One ERP V1
                            if (options.canImportOneErp_v1) {
                                template += `
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item" id="importOneErp_v1" title="Importar do ERP"><i class="fas fa-download" aria-hidden="true"></i> Importar do ERP</a>
                                `;
                            }

                            // Import One ERP
                            if (options.canImportOneErp) {
                                template += `
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item" id="importOneErp" title="Importar do ERP"><i class="fas fa-download"></i> Importar do ERP</a>
                                `;
                            }

                            // Cancelar
                            if (options.canCancelar) {
                                template += `
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item dropdown-item-danger" id="cancelar">
                                        <i class="fas fa-ban" aria-hidden="true"></i> Cancelar
                                    </a>
                                `;
                            }

                            template += `
                                    </div>
                                </span>
                            `;
                        }

                        return template;
                    },
                    createdCell: (td, cellData, rowData, row, col) => {
                        const dropdown = jQuery(td).find('.btn-group');
                        // Ao abrir o dropdown, adicionamos ele no body para exibi-lo corretamente na table
                        dropdown.on('show.bs.dropdown', function() {
                            jQuery('body').append(dropdown.css({
                                    position: 'absolute',
                                    left: dropdown.offset().left,
                                    top: dropdown.offset().top
                                }).detach());
                        });
                        // Ai fechar o dropdown, adicionamos ele novamente a coluna das opções na table
                        dropdown.on('hidden.bs.dropdown', function() {
                            jQuery(td).append(dropdown.css({
                                position: 'relative',
                                left: 'auto',
                                top: 'auto'
                            }).detach());
                        });

                        // Vinculando os eventos de click das opções da tabela
                        const myself = this;
                        // Opções do dropdown
                        dropdown.on('click', 'a', function() {
                            if (this.id === 'edit') {
                                myself.edit(cellData);
                            } else if (this.id === 'consultar') {
                                myself.consultar(cellData);
                            } else if (this.id === 'print') {
                                myself.print(cellData);
                            } else if (this.id === 'cancelar') {
                                myself.cancelar(cellData);
                            } else if (this.id === 'confirmar') {
                                myself.confirmar(cellData)
                            } else if (this.id === 'exportOneErp') {
                                myself.exportOneErp(cellData)
                            } else if (this.id === 'openRetornoAmostras')  {
                                myself.openRetornoAmostras(cellData)
                            } else if (this.id === 'email') {
                                myself.openEmailModal(cellData);
                            } else if (this.id === 'duplicate') {
                                myself.duplicate(cellData);
                            } else if (this.id === 'liberar') {
                                myself.liberar(cellData);
                            } else if (this.id === 'iniciarAguardandoProducao') {
                                myself.iniciarAguardandoProducao(cellData);
                            } else if (this.id === 'terminarAguardandoProducao') {
                                myself.terminarAguardandoProducao(cellData);
                            } else if (this.id === 'liberarExpedicao') {
                                myself.liberarExpedicao(cellData);
                            } else if (this.id === 'importOneEcommerce') {
                                myself.importOneEcommerce(cellData);
                            } else if (this.id === 'importOneErp') {
                                myself.importOneErp(cellData);
                            } else if (this.id === 'importOneErp_v1') {
                                myself.importOneErp_v1(cellData);
                            }
                        });
                    }
                },
                {
                    data: null,
                    className: 'col-badges text-center',
                    serverOrderByColumn: PedidoSearchOrderBy.Numero,
                    visible: this.columnsVisibility.badges,
                    createdCell: (td: any, cellData: any, entity: PedidoSearch) => {
                        if (!entity) {
                            return;
                        }

                        // Criando os badges
                        this.createBadges(entity, td);
                    },
                    render: () => '',
                },
                {
                    data: null,
                    className: 'col-resultado-pedido text-center',
                    serverOrderByColumn: PedidoSearchOrderBy.PercLucroLiquido,
                    visible: this.columnsVisibility.lucroLiquido && this.authService.podeVisualizarQV(), // Exibindo coluna se usuário possuir permissão do primeiro nível de acesso que permite visualizar as informações de resultado que é o QV
                    createdCell: (td: any, cellData: any, entity: PedidoSearch) => {
                        if (!entity || !isVenda(entity.tipoVenda)) { // Somente vendas
                            return;
                        }
                        // Criando wrapper com flex para alinhar os badges dentro da table cell
                        const wrapper = this.renderer.createElement('div');
                        this.renderer.setStyle(wrapper, 'display', 'flex');
                        this.renderer.setStyle(wrapper, 'align-items', 'center');
                        this.renderer.setStyle(wrapper, 'justify-content', 'center');
                        this.renderer.setStyle(wrapper, 'gap', '8px');
                        this.renderer.appendChild(td, wrapper);
                        // Criando badge do lucro líquido do pedido
                        this.createBadgeLucroLiquido(entity, wrapper);
                        // Criando badge da escala de grupo de rentabilidade
                        this.createBadgeEscalaGrupoRentabilidade(entity, wrapper);
                    },
                    render: () => '',
                },
                {
                    data: 'numero',
                    className: 'col-numero text-center',
                    serverOrderByColumn: PedidoSearchOrderBy.Numero,
                    visible: this.columnsVisibility.numero,
                    render: (data: number) => {
                        if (data > 0) {
                            return data;
                        }
                        return '';
                    },
                },
                {
                    data: 'numeroEcommerce',
                    className: 'col-numero-ecommerce text-center',
                    serverOrderByColumn: PedidoSearchOrderBy.NumeroEcommerce,
                    visible: this.columnsVisibility.numeroEcommerce && !this.isRepresentante,
                },
                {
                    data: 'numeroErp',
                    className: 'col-numero-erp text-center',
                    serverOrderByColumn: PedidoSearchOrderBy.NumeroErp,
                    visible: this.columnsVisibility.numeroERP,
                },
                {
                    data: 'dataEmissao',
                    className: 'col-emissao text-center',
                    serverOrderByColumn: PedidoSearchOrderBy.DataEmissaoENumeroENumeroErp,
                    visible: this.columnsVisibility.dataEmissao,
                    render: (data) => this.datePipe.transform(data, 'dd/MM/yyyy - HH:mm'),
                },
                {
                    data: 'dataConfirmacao',
                    className: 'col-data-confirmacao text-center',
                    serverOrderByColumn: PedidoSearchOrderBy.DataConfirmacaoENumeroENumeroErp,
                    visible: this.columnsVisibility.dataConfirmacao,
                    render: (data) => this.datePipe.transform(data, 'dd/MM/yyyy - HH:mm'),
                },
                {
                    data: 'quantidadeTotal',
                    className: 'col-quantidade-total text-right',
                    serverOrderByColumn: PedidoSearchOrderBy.QuantidadeTotal,
                    visible: this.columnsVisibility.quantidadeTotal,
                    render: (data: number) => this.decimalFormatPipe.transform(data, 1, 0, 0),
                },
                {
                    data: 'quantidadeVolumesPrevistos',
                    className: 'col-quantidade-volumes-previstos text-right',
                    serverOrderByColumn: PedidoSearchOrderBy.QuantidadeVolumesPrevistos,
                    visible: this.columnsVisibility.quantidadeVolumesPrevistos,
                    render: (data: number) => this.decimalFormatPipe.transform(data, 1, 0, 0),
                },
                {
                    data: null,
                    className: 'col-valor-total text-right',
                    serverOrderByColumn: PedidoSearchOrderBy.ValorTotal,
                    visible: this.columnsVisibility.valorTotal,
                    createdCell: (td: any, cellData: any, data: PedidoSearch) => {
                        if (data) {
                            // Criando o badge
                            const badgeFactory = this.componentFactoryResolver.resolveComponentFactory(ConfigPedidoTipoVendaBadgeComponent);
                            const badgeRef = this.tipoVendaBadgesContainer.createComponent(badgeFactory);
                            badgeRef.instance.tipoVenda = data.tipoVenda;
                            badgeRef.instance.statusAmostra = data.statusAmostra;
                            badgeRef.instance.isAbreviacao = true;
                            // Inserindo o badge na table cell
                            this.renderer.appendChild(td, badgeRef.location.nativeElement);
                        }
                    },
                    render: (data: PedidoSearch) => `<span class="mr-2">${this.currencyFormatPipe.transform(data?.valorTotal, 1, 2, 2)}</span>`
                },
                {
                    data: null,
                    className: 'col-status text-center',
                    serverOrderByColumn: PedidoSearchOrderBy.Status,
                    visible: this.columnsVisibility.status,
                    createdCell: (td: any, cellData: any, data: PedidoSearch) => {
                        if (data) {
                            // Criando o badge
                            const badgeFactory = this.componentFactoryResolver.resolveComponentFactory(PedidoStatusBadgeComponent);
                            const badgeRef = this.statusBadgesContainer.createComponent(badgeFactory);
                            badgeRef.instance.status = data.status;
                            // Inserindo o badge na table cell
                            this.renderer.appendChild(td, badgeRef.location.nativeElement);
                        }
                    },
                    render: () => ''
                },
                {
                    data: null,
                    className: 'col-setor-gep text-center',
                    serverOrderByColumn: PedidoSearchOrderBy.SetorGep,
                    visible: this.columnsVisibility.setorGep && this.configPedido?.exibirSetorGep,
                    createdCell: (td: any, cellData: any, data: PedidoSearch) => {
                        if (data) {
                            // Criando o badge
                            const badgeFactory = this.componentFactoryResolver.resolveComponentFactory(PedidoSetorGepBadgeComponent);
                            const badgeRef = this.setorGepBadgesContainer.createComponent(badgeFactory);
                            badgeRef.instance.setorGep = data.setorGep;
                            // Expedição
                            badgeRef.instance.dataInicioExpedicao = data.dataInicioExpedicao;
                            badgeRef.instance.dataTerminoExpedicao = data.dataTerminoExpedicao;
                            badgeRef.instance.previsaoSeparacaoExpedicaoDias = data.previsaoSeparacaoExpedicaoDias;

                            // Inserindo o badge na table cell
                            this.renderer.appendChild(td, badgeRef.location.nativeElement);
                        }
                    },
                    render: () => ''
                },
                {
                    data: null,
                    className: 'col-cliente',
                    serverOrderByColumn: PedidoSearchOrderBy.NomeCliente,
                    visible: this.columnsVisibility.cliente,
                    render: (data: PedidoSearch) => {
                        if (data) {
                            return `
                                <span>${this.clienteNomePipe.transform(data.nomePessoa, data.nomeFantasiaPessoa, data.codigoIntegracaoErpPessoa)}</span>
                                <br/>
                                <small>${this.clienteNomeSecundarioPipe.transform(data.nomePessoa, data.nomeFantasiaPessoa)}</small>
                            `;
                        }
                        return '';
                    },
                    createdCell: (td: any, cellData: any, data: PedidoSearch) => {
                        if (data) {
                            td.title = this.clienteNomePipe.transform(data.nomePessoa, data.nomeFantasiaPessoa, data.codigoIntegracaoErpPessoa);
                        }
                    }
                },
                {
                    data: null,
                    className: 'col-classificacao-faturamento-pretendida text-center',
                    serverOrderByColumn: PedidoSearchOrderBy.ClassificacaoFaturamentoPretendida,
                    visible: this.columnsVisibility.classificacaoFaturamentoPretendida,
                    render: (data: PedidoSearch) => {
                        let template = '';
                        if (data && data.idClassificacaoFaturamentoPretendida) {
                            template = data.codigoIntegracaoErpClassificacaoFaturamentoPretendida;
                            if (data.indicadorCustomerSharePretendido) {
                                template += data.indicadorCustomerSharePretendido.toString();
                            }
                        }
                        return template;
                    },
                    createdCell: (td: any, cellData: any, data: PedidoSearch) => {
                        if (data && data.idClassificacaoFaturamentoPretendida) {
                            td.title = `${data.nomeClassificacaoFaturamentoPretendida} ${data.indicadorCustomerSharePretendido ? ' | ' + KPI_CUSTOMER_SHARE.nome + ' ' + this.customerShareIndicadorPipe.transform(data.indicadorCustomerSharePretendido) : ''}`;
                        }
                    }
                },
                {
                    data: null,
                    className: 'col-representante',
                    serverOrderByColumn: PedidoSearchOrderBy.NomeRepresentante,
                    visible: this.columnsVisibility.representante && !this.isRepresentante,
                    render: (data: PedidoSearch) => {
                        if (data) {
                            return `
                                    <span>${this.representanteNomePipe.transform(data.nomeRepresentante, data.nomeFantasiaRepresentante)}</span>
                                    <br/>
                                    <small>${this.representanteNomeSecundarioPipe.transform(data.nomeRepresentante, data.nomeFantasiaRepresentante)}</small>
                                `;
                        }
                        return '';
                    },
                    createdCell: (td: any, cellData: any, data: PedidoSearch) => {
                        if (data) {
                            td.title = this.representanteNomePipe.transform(data.nomeRepresentante, data.nomeFantasiaRepresentante, data.codigoIntegracaoErpRepresentante);
                        }
                    }
                },
                {
                    data: 'motivoReprovado',
                    className: 'col-motivo-reprovado',
                    serverOrderByColumn: PedidoSearchOrderBy.MotivoReprovado,
                    visible: this.columnsVisibility.motivoReprovado,
                    createdCell: (td) => td.title = jQuery(td).html()
                },
                {
                    data: null,
                    className: 'col-tabela-preco',
                    serverOrderByColumn: PedidoSearchOrderBy.NomeTabelaPreco,
                    visible: this.columnsVisibility.tabelaPreco && isModoTabelaPreco(this.configPedido?.modo),
                    render: (data: PedidoSearch) => {
                        let nome = '';
                        // Código
                        if (data?.codigoIntegracaoErpTabelaPreco) {
                            nome += data.codigoIntegracaoErpTabelaPreco;
                        }
                        // Nome
                        if (data?.nomeTabelaPreco) {
                            if (nome !== '') {
                                nome += ' - ';
                            }
                            nome += data.nomeTabelaPreco;
                        }

                        return nome;
                    },
                    createdCell: (td) => td.title = jQuery(td).html()
                },
                {
                    data: null,
                    className: 'col-nome-campanha',
                    serverOrderByColumn: PedidoSearchOrderBy.NomeCampanha,
                    visible: this.columnsVisibility.campanha,
                    render: (data: PedidoSearch) => data.nomeCampanha ?? '',
                    createdCell: (td) => td.title = jQuery(td).html()
                },
                {
                    data: null,
                    className: 'col-nome-condicao-pagamento',
                    serverOrderByColumn: PedidoSearchOrderBy.NomeCondicaoPagamento,
                    visible: this.columnsVisibility.condicaoPagamento,
                    render: (data: PedidoSearch) => {
                        if (!data) {
                            return '';
                        }

                        let template = data.nomeCondicaoPagamento;
                        if (data.prazoMedioCondicaoPagamento) {
                            template += ` (${data.prazoMedioCondicaoPagamento} DD)`
                        }

                        return template;
                    },
                    createdCell: (td) => td.title = jQuery(td).html()
                },
                {
                    data: null,
                    className: 'col-motivo-nao-liberado',
                    serverOrderByColumn: PedidoSearchOrderBy.MotivoNaoLiberado,
                    visible: this.columnsVisibility.motivoNaoLiberado,
                    render: (data: PedidoSearch) => this.pedidoMotivoNaoLiberadoAbreviacaoPipe.transform(data?.motivoNaoLiberado ?? null),
                    createdCell: (td) => td.title = jQuery(td).html()
                },
                {
                    data: 'dataNaoLiberado',
                    className: 'col-data-nao-liberado text-center',
                    visible: this.columnsVisibility.dataNaoLiberado,
                    serverOrderByColumn: PedidoSearchOrderBy.DataNaoLiberado,
                    render: (data) => this.datePipe.transform(data, 'dd/MM/yyyy - HH:mm'),
                },
                {
                    data: null,
                    className: 'col-venda-referenciada text-right',
                    serverOrderByColumn: PedidoSearchOrderBy.ValorRegraNaoLiberado,
                    visible: this.columnsVisibility.vendaReferenciada,
                    render: (data: PedidoSearch) => {
                        if (!data) {
                            return '';
                        }
                        return `${this.currencyFormatPipe.transform(data.valorRegraNaoLiberado ?? 0, 1, 2, 2)} (${this.decimalFormatPipe.transform(data.percBonificacaoNaoLiberado ?? 0, 1, 2, 2)}%)`
                    },
                    createdCell: (td) => td.title = jQuery(td).html()
                },
                {
                    data: null,
                    className: 'col-lucro-liquido-nao-liberado text-right',
                    serverOrderByColumn: PedidoSearchOrderBy.ValorLucroLiquidoNaoLiberado,
                    visible: this.columnsVisibility.lucroLiquidoNaoLiberado && (this.isAdministrador || this.isAtendente),
                    render: (data: PedidoSearch) => {
                        if (!data || !data.valorLucroLiquidoNaoLiberado) {
                            return '';
                        }
                        return `${this.currencyFormatPipe.transform(data.valorLucroLiquidoNaoLiberado, 1, 2, 2)} (${this.decimalFormatPipe.transform(data.percLucroLiquidoNaoLiberado, 1, 2, 2)}%)`
                    },
                    createdCell: (td) => td.title = jQuery(td).html()
                },
                {
                    data: 'dataLiberado',
                    className: 'col-data-liberado text-center',
                    serverOrderByColumn: PedidoSearchOrderBy.DataLiberado,
                    visible: this.columnsVisibility.dataLiberado && this.isAdministrador,
                    render: (data) => this.datePipe.transform(data, 'dd/MM/yyyy - HH:mm'),
                },
                {
                    data: 'nomeUsuarioLiberado',
                    className: 'col-usuario-liberado',
                    serverOrderByColumn: PedidoSearchOrderBy.UsuarioLiberado,
                    visible: this.columnsVisibility.usuarioLiberado && this.isAdministrador,
                    createdCell: (td) => td.title = jQuery(td).html()
                },
                {
                    data: 'justificativaLiberado',
                    className: 'col-justificativa-liberado',
                    serverOrderByColumn: PedidoSearchOrderBy.JustificativaLiberado,
                    visible: this.columnsVisibility.justificativaLiberado && this.isAdministrador,
                    createdCell: (td) => td.title = jQuery(td).html()
                },
            ];

            const columnDefs: any[] = [
                {orderable: false, targets: [0, 1]}
            ];

            this.dataTablesService.showTable(`#${this.tableId}`, this.dataTable, {
                columnDefs,
                columns,
                order: initOrder ? initOrder : [[6, 'desc']], // data_emissao desc
                searching: false,
                info: false,
                serverSide: true,
                processing: true,
                deferRender: true,
                scroller: true,
                scrollX: true,
                // stateSave: true,
                ajax: (data, callback, settings) => {
                    // Order By
                    const order = data?.order[0];
                    const orderColumn = columns[order?.column];
                    this.orderBy = orderColumn?.serverOrderByColumn;
                    this.orderByDir = order?.dir;
                    // Limit
                    const limit = data.length;
                    // Offset
                    const offset = data.start;

                    this.searchSubs$.add(this.pedidoSearchHttpService.search(null, this.filter, this.orderBy, this.orderByDir, limit, offset)
                        .subscribe(
                            entities => {
                                callback({recordsTotal: this.total?.quantidadePedidos, recordsFiltered: this.total?.quantidadePedidos, data: entities});
                            },
                            (error: ApiError) => {
                                this.messageService.error(error.message, 'Erro ao pesquisar os pedidos', error);
                                callback({recordsTotal: 0, recordsFiltered: 0, data: []});
                            }
                        )
                    );
                }
            }).then(dataTable => {
                this.dataTable = dataTable;

                this.dataTable
                    .on('preDraw', () => { // Antes de redesenhar a table
                        // this.clearColBadgesContainer();
                        // Destruindo os badges dos tipos de vendas dos pedidos criados e adicionandos na table
                        // this.clearTipoVendaBadges();
                        // Destruindo os badges dos status dos pedidos criados e adicionandos na table
                        // this.clearStatusBadges();
                        // Destruindo os badges dos setores GEPs dos pedidos criados e adicionandos na table
                        // this.clearsetorGepBadges();
                    });

                setTimeout(() => {
                    // Adicionando evento handler no scroll do body da datatable
                    jQuery(this.el.nativeElement).find('.dataTables_scrollBody').on('scroll', () => {
                        this.visualizacaoStorageService.set(null, null, this.dataTable?.page?.info()?.page, this.dataTable?.order(), this.dataTable?.scroller?.page()?.start, this.visualizacaoStorageKey);
                    });

                    resolve();
                }, 500);
            })
            .catch(() => {});
        });
    }

    /** Atualizando a pesquisa */
    refresh() {
        // Atualizando a pesquisa através da datatable
        this.dataTable?.draw(false);
        // Atualizando os totais
        this.getTotais()
            .then(() => {})
            .catch(() => {});
    }

    /** Verifica se pode editar o Pedido */
    private canEdit(entity: PedidoSearch): boolean {
        return this.pedidoAuthGuardService.canEdit(entity, false, null);
    }

    /** Verifica se pode realizar edições/alterações nas entregas dos itens do pedido */
    private canEditEntregas(entity: PedidoSearch): boolean {
        return this.pedidoAuthGuardService.canEditEntregas(entity);
    }

    edit(entity: PedidoSearch) {
        this.pedidoRouterService.edit(entity.id, this.activatedRoute);
    }

    delete(entity: PedidoSearch) {
        // this.messageService.confirm(`Deseja realmente excluir o pedido ${entity.numero}?`).then(result => {
        //     if (result) {
        //         this.pedidoCrudHttpService.delete(entity.id)
        //             .subscribe(
        //                 response => {
        //                     this.messageService.success('', response);
        //                     this.search();
        //                 },
        //                 (error: ApiError) => this.messageService.error(error.message, `Erro ao excluir o pedido ${entity.numero}`, error)
        //             );
        //     }
        // });
    }

    consultar(entity: PedidoSearch) {
        this.pedidoRouterService.read(entity.id, this.activatedRoute);
    }

    /**  Realiza a confirmação do pedido informado alterando se status de Não Confirmado para Confirmado */
    async confirmar(entity: PedidoSearch) {
        this.messageService.confirm(`Deseja realmente confirmar o pedido ${entity?.numero} e alterar o seu status para Confirmado?`, 'Confirmação').then(async result => {
            if (result) {
                const currentStatus = entity.status;
                entity.status = pedidoConfirmado.id;

                const isConfirmed = await this.pedidoIsLiberadoService.confirmIsLiberado(entity, false);
                if (isConfirmed) {
                    this.pedidoConfirmarHttpService.confirmar(entity?.id)
                        .subscribe(
                            () => {
                                this.messageService.success('', `Pedido ${entity?.numero} confirmado com sucesso`);
                                this.refresh();
                            },
                            (error: ApiError) => this.messageService.error(error.message, `Erro ao confirmar o pedido ${entity?.numero}`, error)
                        );
                } else {
                    entity.status = currentStatus;
                }
            }
        });
    }

    exportOneErp(entity: PedidoSearch) {
        this.pedidoRouterService.navigate([entity.id], {exportingerp: true}, this.activatedRoute);
    }

    print(entity: PedidoSearch) {
        this.pedidoReportService.print(entity.id);
    }

    /** Abre o modal para enviar o e-mail do pedido */
    openEmailModal(entity: PedidoSearch) {
        this.pedidoEmailModal.open(entity?.id);
    }

    duplicate(entity: PedidoSearch) {
        this.pedidoRouterService.duplicate(entity.id, entity.numero, this.activatedRoute);
    }

    /** Abre o modal para liberar o pedido */
    liberar(entity: PedidoSearch) {
        this.pedidoLiberarModal.open(entity.id);
    }

    /** Após liberar um pedido, atualizamos a tabela */
    afterLiberar(entity: Pedido) {
        this.refresh();
        this.afterLiberarPedido.emit(entity);
    }

    /** Iniciar pedido no aguardando produção */
    iniciarAguardandoProducao(entity: PedidoSearch) {
        this.messageService.confirm(`Deseja realmente aguardar a produção do pedido ${entity.numero}?`, 'Confirme').then(result => {
            if (result) {
                this.pedidoSetorGepAguardandoProducaoHttpService.iniciar(entity.id)
                    .subscribe(
                        response => {
                            this.messageService.success('', response);
                            this.refresh();
                        },
                        (error: ApiError) => this.messageService.error(error.message, `Erro ao aguardar o pedido ${entity.numero} na produção`)
                    );
            }
        })
    }

    /** Termina pedido no aguardando produção */
    terminarAguardandoProducao(entity: PedidoSearch) {
        this.messageService.confirm(`Deseja realmente liberar a produção do pedido ${entity.numero}?`, 'Confirme').then(result => {
            if (result) {
                this.pedidoSetorGepAguardandoProducaoHttpService.terminar(entity.id)
                    .subscribe(
                        response => {
                            this.messageService.success('', response);
                            this.refresh();
                        },
                        (error: ApiError) => this.messageService.error(error.message, `Erro ao liberar o pedido ${entity.numero} da produção`)
                    );
            }
        })
    }

    /** Libera o pedido informado para expedição */
    liberarExpedicao(entity: PedidoSearch) {
        this.messageService.confirm(`Deseja realmente liberar o pedido ${entity.numero} para expedição?`, 'Confirme').then(result => {
            if (result) {
                this.expedicaoHttpService.liberar(entity.id)
                    .subscribe(
                        () => {
                            this.messageService.success('', `Pedido ${entity.numero} liberado para expedição com sucesso`);
                            this.refresh();
                        },
                        (error: ApiError) => this.messageService.error(error.message, `Erro ao liberar o pedido ${entity.numero} para expedição`)
                    );
            }
        })
    }

    /** Abre o modal para cancelar o pedido */
    cancelar(entity: PedidoSearch) {
        this.pedidoCancelamentoModal.open(entity?.id);
    }

    /** Após cancelar um pedido, atualizamos a tabela */
    afterCancel() {
        this.afterCancelar.emit();
        this.refresh();
    }

    /** Executa a importação e sincronização do pedido do e-commerce */
    importOneEcommerce(entity: PedidoSearch) {
        if (!entity) {
            return;
        }

        const numeroPedido = entity.numero ? entity.numero : entity.numeroEcommerce;
        const task = new Task(`Sincronizando pedido ${numeroPedido}`, `Executando sincronização do pedido ${numeroPedido} com o e-commerce`);
        this.taskService.add(task, task.description);
        this.pedidoIntegracaoEcommerceImportHttpService.importOne(entity.idIntegracaoEcommerce, entity.numeroEcommerce)
            .subscribe(
                () => {
                    this.taskService.success(task, `Sincronização do pedido ${numeroPedido} com o e-commerce realizada com sucesso`);
                    this.refresh();
                },
                (error: ApiError) => this.taskService.error(task, `Erro ao realizar a sincronização do pedido ${numeroPedido} com o e-commerce`, error.cause, error)
            );
    }

    /** Após realizar a importação dos pedidos do e-commerce */
    private afterImportEcommerce() {
        this.afterImportEcommerceSub$ = this.pedidoIntegracaoEcommerceImportService.afterImport
                                            .subscribe(
                                                () => this.refresh()
                                            );
    }

    /** Executa a integração de leitura individual dos dados do pedido no ERP */
    importOneErp_v1(entity: PedidoSearch) {
        if (!entity) {
            return;
        }

        const numeroPedido = entity.numero ? entity.numero : entity.numeroErp;
        const task = new Task(`Sincronizando pedido ${numeroPedido}`, `Executando sincronização do pedido ${numeroPedido} com o sistema ERP`);
        this.taskService.add(task, task.description);
        this.integrationPedidoHttpService.execReadIndividualIntegration(entity.id)
            .subscribe(
                () => {
                    this.taskService.success(task, `Sincronização do pedido ${numeroPedido} com o sistema ERP realizada com sucesso`);
                    this.refresh();
                },
                (error: ApiError) => this.taskService.error(task, `Erro ao realizar a sincronização do pedido ${numeroPedido} com o sistema ERP`, error.message, error)
            );
    }

    /** Após realizar a exportação do pedido para o ERP */
    private afterExportOneErp() {
        // Subscrevendo ao evento de finalização da integração do pedido com o ERP
        this.afterExportOneErp$ = this.pedidoIntegrationErpService.afterExportOneErp
                                            .subscribe(
                                                () => this.refresh()
                                            );
    }

    /** Executa a importação do pedido do ERP */
    importOneErp(entity: PedidoSearch) {
        if (!entity) {
            return;
        }

        const numeroPedido = entity.numero ? entity.numero : entity.numeroErp;
        const task = new Task(`Importando pedido ${numeroPedido}`, `Executando importação do pedido ${numeroPedido} com o ERP`);
        this.taskService.add(task, task.description);
        this.pedidoIntegracaoErpImportHttpService.importOne(entity.id)
            .subscribe(
                () => {
                    this.taskService.success(task, `Importação do pedido ${numeroPedido} com o ERP realizada com sucesso`);
                    this.refresh();
                },
                (error: ApiError) => this.taskService.error(task, `Erro ao realizar a importação do pedido ${numeroPedido} com o ERP`, error.cause, error)
            );
    }

    /** Após realizar a importação dos pedidos do ERP */
    private afterImportErp() {
        this.afterImportErpSub$ = this.pedidoIntegracaoErpImportService.afterImport
                                            .subscribe(
                                                () => this.refresh()
                                            );
    }

    openRetornoAmostras(entity: PedidoSearch) {
        this.pedidoRouterService.openRetornoAmostras(entity.id, this.activatedRoute);
    }

    ngOnDestroy() {
        this.searchSubs$?.unsubscribe();
        this.afterImportEcommerceSub$?.unsubscribe();
        this.afterImportErpSub$?.unsubscribe();
        this.afterExportOneErp$?.unsubscribe();

        // Removendo evento de scroll do body da datatable
        jQuery(this.el.nativeElement).find('.dataTables_scrollBody').off('scroll');

        this.clearBadges();
        this.clearTipoVendaBadges();
        this.clearStatusBadges();
        this.clearSetorGepBadges();

    }
}
